import { invokeEdgeFunction } from '@/utils/edgeFunctionUtils';

const CANVA_AUTH_ENDPOINTS = {
  AUTHORIZE: '/canva-authorize',
  REVOKE: '/canva-revoke', 
  TOKEN: '/canva-token',
};

export interface CanvaAuthState {
  isAuthorized: boolean;
  token?: string;
  userId?: string;
  displayName?: string;
}

// Simple redirect-based OAuth flow (no popup)
export const startCanvaOAuth = async (): Promise<void> => {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  
  if (!supabaseUrl) {
    throw new Error('VITE_SUPABASE_URL environment variable is not configured');
  }
  
  console.log('🎨 [Canva Auth] Starting OAuth redirect flow...');
  
  // Store current location for return after OAuth
  sessionStorage.setItem('canva_return_url', window.location.pathname);
  
  // Get the current user's session
  const { supabase } = await import('@/integrations/supabase/client');
  const { data: { session } } = await supabase.auth.getSession();
  
  if (!session) {
    throw new Error('User must be logged in to connect to Canva');
  }
  
  // Store user ID for later verification
  sessionStorage.setItem('supabase_user_id', session.user.id);
  
  // Call the Edge Function with auth to get the OAuth URL
  const { data, error } = await invokeEdgeFunction('canva-authorize', {}, {
    'Authorization': `Bearer ${session.access_token}`
  });
  
  if (error || !data?.auth_url) {
    throw new Error('Failed to start OAuth flow');
  }
  
  // Redirect to Canva OAuth URL
  window.location.href = data.auth_url;
};

// Legacy function kept for compatibility - now just calls startCanvaOAuth
export const getCanvaAuthorization = async (): Promise<string | undefined> => {
  await startCanvaOAuth();
  return undefined; // This won't return since we're redirecting
};

export const revokeCanvaAuthorization = async (): Promise<boolean> => {
  try {
    const canvaUserId = sessionStorage.getItem('canva_user_id');
    
    if (!canvaUserId) {
      // No user to revoke, but that's considered success
      return true;
    }
    
    const { data, error } = await invokeEdgeFunction('canva-revoke', { canvaUserId });
    
    if (error) {
      console.error('Failed to revoke authorization:', error);
      return false;
    }
    
    // Clear session storage
    sessionStorage.removeItem('canva_user_id');
    sessionStorage.removeItem('canva_display_name');
    
    return data?.success === true;
  } catch (error) {
    console.error('Error revoking authorization:', error);
    return false;
  }
};

export const checkForAccessToken = async (): Promise<{ token?: string }> => {
  try {
    // Get current Supabase user
    const { supabase } = await import('@/integrations/supabase/client');
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session?.user?.id) {
      return { token: undefined };
    }
    
    const { data, error } = await invokeEdgeFunction('canva-token', {}, {
      'Authorization': `Bearer ${session.access_token}`
    });
    
    if (error || !data?.token) {
      return { token: undefined };
    }
    
    return { token: data.token };
  } catch (error) {
    console.error('Error checking access token:', error);
    return { token: undefined };
  }
};

export const getCanvaUserProfile = async (): Promise<{ displayName?: string; userId?: string }> => {
  try {
    const canvaUserId = sessionStorage.getItem('canva_user_id');
    const cachedDisplayName = sessionStorage.getItem('canva_display_name');
    
    if (!canvaUserId) {
      return {};
    }
    
    // Try to get fresh profile from API
    const { data, error } = await invokeEdgeFunction('canva-user-profile', { canvaUserId });
    
    if (error || !data) {
      // Fall back to cached data
      return {
        displayName: cachedDisplayName || undefined,
        userId: canvaUserId,
      };
    }
    
    // Update cached display name if it changed
    if (data.display_name && data.display_name !== cachedDisplayName) {
      sessionStorage.setItem('canva_display_name', data.display_name);
    }
    
    return {
      displayName: data.display_name,
      userId: data.user_id,
    };
  } catch (error) {
    console.error('Error getting user profile:', error);
    
    // Fall back to cached data
    const canvaUserId = sessionStorage.getItem('canva_user_id');
    const cachedDisplayName = sessionStorage.getItem('canva_display_name');
    
    return {
      displayName: cachedDisplayName || undefined,
      userId: canvaUserId || undefined,
    };
  }
};